import { Fragment, useEffect, useMemo, useState } from "react";

import { SimpleGrid } from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";
import { useDebouncedCallback } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { RadioGroup, Select, TextInput } from "components";
import {
  BankCreditRequestForm,
  BankCreditResponse,
  BankCreditHousingResponse,
  HousingCostSharingWith,
  HousingPersonOnMortgageContract,
} from "types";
import { AppUtils } from "utils";

const HOUSING_COST_SHARING_WITH_OPTIONS: HousingCostSharingWith[] = [
  "PARENTS",
  "PARTNER",
  "BROTHER",
  "SISTER",
  "OTHER",
];

const HOUSING_PERSON_ON_MORTGAGE_CONTRACT_OPTIONS: HousingPersonOnMortgageContract[] = [
  "YES",
  "NO",
  "OTHER_PROOF",
];

interface HousingCostSharingIndividualProps {
  credit: BankCreditResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  readOnly?: boolean;
  housingExpense?: "RENT" | "MORTGAGE";
}

const tempHousingCostSharingObject: Partial<BankCreditHousingResponse> = {
  costSharingWith: undefined,
  costSharingPerson: undefined,
  personOnMortgageContract: undefined,
};

const propertyName = "housingExpenses";

export const HousingCostSharingIndividual = ({
  credit,
  form,
  readOnly = false,
  housingExpense,
}: HousingCostSharingIndividualProps) => {
  const { t } = useTranslation();
  const [housingCostSharingVisibleFields, setHousingCostSharingVisibleFields] = useState<string[]>(
    AppUtils.getVisibleHousingCostSharingFields(credit[propertyName]),
  );

  const housingCostSharingForm = useForm<BankCreditHousingResponse[]>({
    mode: "uncontrolled",
    initialValues:
      credit[propertyName] && credit[propertyName].length > 0
        ? credit[propertyName].map((el) => ({ ...el }))
        : [],
    onValuesChange(values, previous) {
      updateCredit(values, previous);
    },
  });

  // Sync array size with housingCostSplitCount
  useEffect(() => {
    const formValues = form.getValues();
    if (Object.keys(formValues).length === 0) {
      return;
    }

    const splitCount = formValues.housingCostSplitCount;
    if (splitCount) {
      const requiredLength = splitCount - 1;
      const currentValues = housingCostSharingForm.getTransformedValues();
      const currentLength = Object.keys(currentValues).length;

      if (requiredLength !== currentLength) {
        const newArray: Partial<BankCreditHousingResponse>[] = [];

        // Preserve existing entries up to requiredLength
        for (let i = 0; i < requiredLength; i++) {
          if (i < currentLength && currentValues[i]) {
            newArray.push({ ...currentValues[i] });
          } else {
            newArray.push({ ...tempHousingCostSharingObject });
          }
        }

        housingCostSharingForm.setInitialValues(newArray as BankCreditHousingResponse[]);
        housingCostSharingForm.reset();
      }
    } else {
      // If no split count, clear the array
      if (Object.keys(housingCostSharingForm.getTransformedValues()).length > 0) {
        housingCostSharingForm.setInitialValues([]);
        housingCostSharingForm.reset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getValues().housingCostSplitCount]);

  const updateCredit = useDebouncedCallback(
    async (values: BankCreditHousingResponse[], previous: BankCreditHousingResponse[]) => {
      const prevVisibleFields = AppUtils.getVisibleHousingCostSharingFields(previous);
      const newVisibleFields = AppUtils.getVisibleHousingCostSharingFields(values);
      AppUtils.clearArrayFieldsWhenUpdating(values, previous, newVisibleFields);
      AppUtils.updateArrayFieldsWhenVisibilityChanges(
        prevVisibleFields,
        newVisibleFields,
        values,
        housingCostSharingForm,
      );

      const tempArray: Partial<BankCreditHousingResponse>[] = [];
      Object.keys(values).forEach((key) => {
        const item: Partial<BankCreditHousingResponse> = { ...values[Number(key)] };
        const isNotEmpty =
          item.costSharingWith ||
          item.costSharingPerson ||
          item.personOnMortgageContract !== undefined;
        if (isNotEmpty) {
          item.creditRequestId = credit.id;
          tempArray.push(item);
        }
      });
      form.setFieldValue(propertyName, tempArray as BankCreditHousingResponse[]);
      setHousingCostSharingVisibleFields(newVisibleFields);
    },
    300,
  );

  const housingCostSharingWithOptions = useMemo(
    () =>
      HOUSING_COST_SHARING_WITH_OPTIONS.map((el) => ({
        value: el,
        label: t(`bankCredit.housingCostSharingWith.options.${el}`),
      })),
    [t],
  );

  const housingPersonOnMortgageContractOptions = useMemo(
    () =>
      HOUSING_PERSON_ON_MORTGAGE_CONTRACT_OPTIONS.map((el) => ({
        value: el,
        label: t(`bankCredit.housingPersonOnMortgageContract.options.${el}`),
      })),
    [t],
  );

  const transformed = housingCostSharingForm.getTransformedValues();

  return (
    <SimpleGrid cols={4}>
      {Object.keys(transformed).map((el: string, index: number) => (
        <Fragment key={el}>
          <Select
            label={t("bankCredit.housingCostSharingWith.label")}
            data={housingCostSharingWithOptions}
            key={housingCostSharingForm.key(`${index}.costSharingWith`)}
            {...housingCostSharingForm.getInputProps(`${index}.costSharingWith`)}
            clearable={true}
            disabled={readOnly}
          />

          {housingCostSharingVisibleFields.includes(
            `housingExpenses.${index}.costSharingPerson`,
          ) && (
            <TextInput
              label={t("bankCredit.housingCostSharingPerson.label")}
              placeholder={t("bankCredit.housingCostSharingPerson.placeholder")}
              key={housingCostSharingForm.key(`${index}.costSharingPerson`)}
              {...housingCostSharingForm.getInputProps(`${index}.costSharingPerson`)}
              disabled={readOnly}
            />
          )}

          <RadioGroup
            label={
              housingExpense === "RENT"
                ? t("bankCredit.housingPersonOnContract.label")
                : t("bankCredit.housingPersonOnMortgage.label")
            }
            fieldName={`${index}.personOnMortgageContract`}
            form={housingCostSharingForm}
            disabled={readOnly}
            options={housingPersonOnMortgageContractOptions}
          />
        </Fragment>
      ))}
    </SimpleGrid>
  );
};
